import type { DataProvider, BaseRecord } from '@refinedev/core';
import type {
  ApiKey,
  ApiKeyListResponse,
  CreateApiKeyResponse,
  CreatePromptBody,
  DbConnectorCreateBody,
  DbConnectorItem,
  DbConnectorListResponse,
  DbConnectorUpdateBody,
  DocumentDetail,
  DocumentListQuery,
  DocumentListResponse,
  RelationBody,
  RelationListQuery,
  RelationListResponse,
  TfPromptRow,
  TfTrackRow,
  TrackManifest,
  UpdatePromptBody,
  UpdateUserBody,
  UserListResponse,
  UserResponse,
} from '@verybigthings/trackframe-shared/schemas';
import { asBoolean } from './list-filters';
import { hub } from './hub-client';

// The hub's admin endpoints don't paginate — they return whole collections —
// so this provider serves the entire list and lets refine handle client-side
// paging. Filters and sorters are likewise applied in-memory; the volume here
// is operator-scale (handfuls to hundreds of rows), so this is fine.

type Row = BaseRecord & Record<string, unknown>;

// In-memory filter support for the whole-collection resources. Handles the
// logical-filter operators the list toolbars use; `field: 'q'` is a free-text
// search across every string field of the row.
function applyFilters<T extends Row>(rows: T[], filters: unknown): T[] {
  const list = (filters ?? []) as { field?: string; operator?: string; value?: unknown }[];
  if (list.length === 0) return rows;
  return rows.filter((row) =>
    list.every((f) => {
      if (!f.field) return true;
      if (f.field === 'q') {
        const needle = String(f.value ?? '').toLowerCase();
        if (!needle) return true;
        return Object.values(row).some(
          (v) => typeof v === 'string' && v.toLowerCase().includes(needle),
        );
      }
      const v = (row as Record<string, unknown>)[f.field];
      switch (f.operator) {
        case 'contains':
          if (Array.isArray(v)) return v.includes(f.value);
          return typeof v === 'string' && v.toLowerCase().includes(String(f.value ?? '').toLowerCase());
        case 'eq':
          return v === f.value;
        case 'null':
          return v === null || v === undefined;
        case 'nnull':
          return v !== null && v !== undefined;
        default:
          return true;
      }
    }),
  );
}

function paginate<T extends Row>(rows: T[], pagination: unknown): { data: T[]; total: number } {
  const p = pagination as { currentPage?: number; current?: number; pageSize?: number; mode?: string } | undefined;
  if (p?.mode === 'off') {
    return { data: rows, total: rows.length };
  }
  const current = p?.currentPage ?? p?.current ?? 1;
  const pageSize = p?.pageSize ?? 50;
  const start = (current - 1) * pageSize;
  return { data: rows.slice(start, start + pageSize), total: rows.length };
}

// The /relations route filters SERVER-side (RelationListQuerySchema). Map
// refine's `eq` CrudFilters onto its query params — anything else (other
// operators, unknown fields, empty values) is dropped, never filtered
// client-side.
const RELATION_QUERY_FIELDS = [
  'object',
  'objectType',
  'objectId',
  'relation',
  'subject',
  'subjectType',
  'subjectId',
  'subjectRelation',
  'q',
] as const;

// `includeStructural` is the one boolean the route takes — it is opt-OUT, so
// only `false` is worth sending (see STRUCTURAL_RELATIONS in the hub).
const RELATION_BOOLEAN_FIELDS = ['includeStructural'] as const;

export function relationFiltersToQuery(filters: unknown): Partial<RelationListQuery> {
  const list = (filters ?? []) as { field?: string; operator?: string; value?: unknown }[];
  const query: Record<string, string | boolean> = {};
  for (const f of list) {
    if (f.operator !== 'eq' || !f.field) continue;
    if ((RELATION_BOOLEAN_FIELDS as readonly string[]).includes(f.field)) {
      // `asBoolean`, not a `typeof` check: a filter restored from the URL
      // arrives as the STRING 'false', and dropping it silently un-hid the
      // structural tuples on every reload.
      const flag = asBoolean(f.value);
      if (flag !== undefined) query[f.field] = flag;
      continue;
    }
    if (!(RELATION_QUERY_FIELDS as readonly string[]).includes(f.field)) continue;
    const value = typeof f.value === 'string' ? f.value.trim() : '';
    if (!value) continue;
    query[f.field] = value;
  }
  return query as Partial<RelationListQuery>;
}

// M28 (ADM-12, COP-117): GET /documents filters SERVER-side
// (DocumentListQuerySchema). Same mapping shape as `relationFiltersToQuery` —
// refine's `eq` CrudFilters onto the query params; anything else is dropped,
// never filtered client-side. The per-document `read` check does the scoping.
const DOCUMENT_QUERY_FIELDS = ['status', 'trackId', 'ownerUserId'] as const;

export function documentFiltersToQuery(filters: unknown): Partial<DocumentListQuery> {
  const list = (filters ?? []) as { field?: string; operator?: string; value?: unknown }[];
  const query: Record<string, string> = {};
  for (const f of list) {
    if (!f.field) continue;
    const value = typeof f.value === 'string' ? f.value.trim() : '';
    if (!value) continue;
    // Document name is a SUBSTRING search (`contains`); every other filter is
    // an exact id or enum (`eq`).
    if (f.field === 'filename') {
      if (f.operator === 'contains') query.filename = value;
      continue;
    }
    if (f.operator !== 'eq') continue;
    if (!(DOCUMENT_QUERY_FIELDS as readonly string[]).includes(f.field)) continue;
    query[f.field] = value;
  }
  return query as Partial<DocumentListQuery>;
}

// Refine's DataProvider uses a generic TData on every method, so we cast to a
// shared response shape. The runtime values match — only TS narrowing differs.
type ListResp = { data: never; total: number; searchTruncated?: boolean };

function notSupported(action: string, resource: string): never {
  throw new Error(`${action} is not supported for resource "${resource}"`);
}

export const dataProvider: DataProvider = {
  getApiUrl: () => '',

  getList: async ({ resource, pagination, filters, meta }) => {
    switch (resource) {
      case 'users': {
        const res: UserListResponse = await hub.usersList();
        return paginate(applyFilters(res.users as Row[], filters), pagination) as ListResp;
      }
      case 'api-keys': {
        const res: ApiKeyListResponse = await hub.apiKeysList();
        return paginate(applyFilters(res.apiKeys as Row[], filters), pagination) as ListResp;
      }
      case 'tracks': {
        const rows = (await hub.tracksList()) as TfTrackRow[];
        return paginate(applyFilters(rows as Row[], filters), pagination) as ListResp;
      }
      case 'prompts': {
        const trackId = (meta as { trackId?: string } | undefined)?.trackId;
        const rows = (await hub.promptsList({ query: trackId ? { trackId } : {} })) as Row[];
        return paginate(applyFilters(rows, filters), pagination) as ListResp;
      }
      // UI resource renamed Relations → Permissions (M27, §4 D-11). The hub
      // API + `relations*` client methods are UNCHANGED — only the resource
      // key the SPA dispatches on moved.
      case 'permissions': {
        const p = pagination as { currentPage?: number; current?: number; pageSize?: number; mode?: string } | undefined;
        const current = p?.currentPage ?? p?.current ?? 1;
        const pageSize = p?.pageSize ?? 50;
        const limit = Math.min(pageSize, 500);
        const offset = (current - 1) * pageSize;
        const res: RelationListResponse = await hub.relationsList({
          query: { ...relationFiltersToQuery(filters), limit, offset },
        });
        // `searchTruncated` rides along so the list can tell the operator the
        // search was capped — refine ignores keys it does not know.
        return {
          data: res.relations as never,
          total: res.total,
          searchTruncated: res.searchTruncated,
        } as ListResp;
      }
      case 'models': {
        const rows = (await hub.llmModelsList()) as Row[];
        return paginate(applyFilters(rows, filters), pagination) as ListResp;
      }
      case 'db-connectors': {
        const res: DbConnectorListResponse = await hub.dbConnectorsList();
        return paginate(applyFilters(res.connectors as Row[], filters), pagination) as ListResp;
      }
      // M28 (ADM-12, COP-117): server-side filtered + paginated, like
      // `permissions` above. Filters (status/trackId/ownerUserId) map onto the
      // DocumentListQuery params; the hub returns only rows the caller can read.
      case 'documents': {
        const p = pagination as { currentPage?: number; current?: number; pageSize?: number; mode?: string } | undefined;
        const current = p?.currentPage ?? p?.current ?? 1;
        const pageSize = p?.pageSize ?? 50;
        const limit = Math.min(pageSize, 500);
        const offset = (current - 1) * pageSize;
        const res: DocumentListResponse = await hub.documentsList({
          query: { ...documentFiltersToQuery(filters), limit, offset },
        });
        return { data: res.items as never, total: res.total } as ListResp;
      }
      default:
        return notSupported('getList', resource);
    }
  },

  getOne: async ({ resource, id }) => {
    const sid = String(id);
    switch (resource) {
      case 'users': {
        const res: UserResponse = await hub.usersGet({ params: { id: sid } });
        return { data: res.user as never };
      }
      case 'tracks': {
        const row = (await hub.tracksGet({ params: { id: sid } })) as TfTrackRow;
        return { data: row as never };
      }
      case 'prompts': {
        const row = await hub.promptsGet({ params: { id: sid } });
        return { data: row as never };
      }
      case 'db-connectors': {
        // There is NO GET /db-connectors/:id — the list payload carries
        // everything the Show page needs (the schema comes separately from
        // hub.dbConnectorsSchema), so getOne derives from the list by id.
        const res: DbConnectorListResponse = await hub.dbConnectorsList();
        const row = res.connectors.find((c: DbConnectorItem) => c.id === sid);
        if (!row) throw new Error(`Connector "${sid}" not found`);
        return { data: row as never };
      }
      case 'models': {
        const row = await hub.llmModelGet({ params: { id: sid } });
        return { data: row as never };
      }
      // M28 (ADM-12): GET /documents/:id returns the extended detail (summary
      // + readiness + tuple-derived access summary), `read`-gated per document.
      case 'documents': {
        const res: DocumentDetail = await hub.documentsGet({ params: { id: sid } });
        return { data: res as never };
      }
      // api-keys / permissions have no getOne endpoint.
      default:
        return notSupported('getOne', resource);
    }
  },

  create: async ({ resource, variables }) => {
    switch (resource) {
      case 'api-keys': {
        const res: CreateApiKeyResponse = await hub.apiKeysCreate({
          body: variables as { name: string; userId?: string },
        });
        // Returns the plaintext exactly once — the Create page surfaces it
        // via PlaintextSecretDialog. We pass it through the data prop.
        return { data: res as never };
      }
      case 'tracks': {
        // HUB-062 / DEV-014: registration is a manifest upsert. The admin
        // Create form supplies the manifest fields directly; we PUT to the
        // track's id. Generate one client-side if the form didn't (so the
        // SPA can register a fresh track without forcing the operator to
        // hand-pick a UUID).
        const v = variables as Partial<TrackManifest> & { openapi?: Record<string, unknown> };
        const id = v.id ?? crypto.randomUUID();
        const body: TrackManifest = {
          schemaVersion: 1,
          id,
          name: v.name ?? '',
          displayName: v.displayName ?? v.name ?? '',
          ...(v.description !== undefined ? { description: v.description } : {}),
          apiUrl: v.apiUrl ?? '',
          ...(v.baseUrl !== undefined ? { baseUrl: v.baseUrl } : {}),
          ...(v.healthCheckUrl !== undefined ? { healthCheckUrl: v.healthCheckUrl } : {}),
          openapi: v.openapi ?? { openapi: '3.0.0', info: { title: v.name ?? 'track', version: '1' }, paths: {} },
          ...(v.systemPrompt !== undefined ? { systemPrompt: v.systemPrompt } : {}),
        };
        const row = (await hub.tracksUpsert({ params: { id }, body })) as TfTrackRow;
        return { data: row as never };
      }
      case 'prompts': {
        const row = await hub.promptsCreate({
          body: variables as CreatePromptBody,
        });
        return { data: row as never };
      }
      case 'permissions': {
        const tuple = variables as RelationBody;
        await hub.relationsWrite({ body: tuple });
        // Synthesize an id so refine's list-cache invalidation is happy.
        const synthetic = {
          id: `${tuple.object}/${tuple.relation}/${tuple.subject}`,
          ...tuple,
        };
        return { data: synthetic as never };
      }
      case 'db-connectors': {
        const row: DbConnectorItem = await hub.dbConnectorsCreate({
          body: variables as DbConnectorCreateBody,
        });
        return { data: row as never };
      }
      default:
        return notSupported('create', resource);
    }
  },

  update: async ({ resource, id, variables }) => {
    const sid = String(id);
    switch (resource) {
      case 'users': {
        const res: UserResponse = await hub.usersUpdate({
          params: { id: sid },
          body: variables as UpdateUserBody,
        });
        return { data: res.user as never };
      }
      case 'tracks': {
        // HUB-062 / DEV-014: edits are also manifest upserts. The PATCH
        // endpoint is going away; we synthesize the full manifest by
        // fetching the existing row and overlaying the form's diff.
        const existing = (await hub.tracksGet({ params: { id: sid } })) as TfTrackRow;
        const v = variables as Partial<TrackManifest> & { openapi?: Record<string, unknown> };
        const body: TrackManifest = {
          schemaVersion: 1,
          id: existing.id,
          name: v.name ?? existing.name,
          displayName: v.displayName ?? existing.displayName ?? existing.name,
          ...(((v.description ?? existing.description) !== null && (v.description ?? existing.description) !== undefined)
            ? { description: (v.description ?? existing.description) as string }
            : {}),
          apiUrl: v.apiUrl ?? existing.apiUrl,
          ...(((v.baseUrl ?? existing.baseUrl) !== null && (v.baseUrl ?? existing.baseUrl) !== undefined)
            ? { baseUrl: (v.baseUrl ?? existing.baseUrl) as string }
            : {}),
          ...(((v.healthCheckUrl ?? existing.healthCheckUrl) !== null && (v.healthCheckUrl ?? existing.healthCheckUrl) !== undefined)
            ? { healthCheckUrl: (v.healthCheckUrl ?? existing.healthCheckUrl) as string }
            : {}),
          openapi: v.openapi ?? (existing.openapiSpecJson ?? { openapi: '3.0.0', info: { title: existing.name, version: '1' }, paths: {} }),
          ...(((v.systemPrompt ?? existing.systemPrompt) !== null && (v.systemPrompt ?? existing.systemPrompt) !== undefined)
            ? { systemPrompt: (v.systemPrompt ?? existing.systemPrompt) as string }
            : {}),
        };
        const row = (await hub.tracksUpsert({ params: { id: sid }, body })) as TfTrackRow;
        return { data: row as never };
      }
      case 'prompts': {
        const row: TfPromptRow = await hub.promptsUpdate({
          params: { id: sid },
          body: variables as UpdatePromptBody,
        });
        return { data: row as never };
      }
      case 'db-connectors': {
        const row: DbConnectorItem = await hub.dbConnectorsUpdate({
          params: { id: sid },
          body: variables as DbConnectorUpdateBody,
        });
        return { data: row as never };
      }
      default:
        return notSupported('update', resource);
    }
  },

  deleteOne: async ({ resource, id, variables }) => {
    const sid = String(id);
    switch (resource) {
      case 'api-keys': {
        const before = await dataProvider
          .getList({ resource: 'api-keys', pagination: { mode: 'off' } as never })
          .catch(() => ({ data: [] as ApiKey[] }));
        await hub.apiKeysDelete({ params: { id: sid } });
        const removed = (before.data as unknown as ApiKey[]).find((r) => r.id === sid);
        return { data: (removed ?? { id: sid }) as never };
      }
      case 'tracks': {
        await hub.tracksDelete({ params: { id: sid } });
        return { data: { id: sid } as never };
      }
      case 'prompts': {
        await hub.promptsDelete({ params: { id: sid } });
        return { data: { id: sid } as never };
      }
      case 'permissions': {
        const tuple = variables as RelationBody;
        if (!tuple) throw new Error('Permissions delete requires a tuple body');
        await hub.relationsDelete({ body: tuple });
        return { data: { id: sid, ...tuple } as never };
      }
      case 'db-connectors': {
        await hub.dbConnectorsDelete({ params: { id: sid } });
        return { data: { id: sid } as never };
      }
      default:
        return notSupported('deleteOne', resource);
    }
  },

  getMany: async ({ resource, ids }) => {
    if (resource === 'users') {
      const rows = await Promise.all(
        ids.map((id) =>
          hub.usersGet({ params: { id: String(id) } }).then((r: UserResponse) => r.user),
        ),
      );
      return { data: rows as never };
    }
    if (resource === 'tracks') {
      const rows = await Promise.all(
        ids.map((id) => hub.tracksGet({ params: { id: String(id) } })),
      );
      return { data: rows as never };
    }
    if (resource === 'prompts') {
      const rows = await Promise.all(
        ids.map((id) => hub.promptsGet({ params: { id: String(id) } })),
      );
      return { data: rows as never };
    }
    return notSupported('getMany', resource);
  },

  custom: async ({ url, method, payload }) => {
    // Lifecycle and odd-shaped actions are exposed via custom() so the call
    // sites don't need a parallel surface. We map url→endpoint here.
    if (url.startsWith('users/') && url.endsWith('/disable') && method === 'post') {
      const id = url.split('/')[1] ?? '';
      const res: UserResponse = await hub.usersDisable({ params: { id } });
      return { data: res.user as never };
    }
    if (url.startsWith('users/') && url.endsWith('/reactivate') && method === 'post') {
      const id = url.split('/')[1] ?? '';
      const res: UserResponse = await hub.usersReactivate({ params: { id } });
      return { data: res.user as never };
    }
    if (url === 'prompts/upsert-defaults' && method === 'post') {
      const res = await hub.promptsUpsertDefaults({ body: payload as never });
      return { data: res as never };
    }
    throw new Error(`Custom action not supported: ${method.toUpperCase()} ${url}`);
  },
};
